import { SelectedPoolType } from "@/atoms/shared"
import { SolendUSDSObligationListType, SolendUSDSObligationsStatsType } from "@/atoms/solendObligations"
import * as anchor from "@project-serum/anchor0.24.2"
import NodeWallet from "@project-serum/anchor0.24.2/dist/cjs/nodewallet"
import {
  BlockchainNetwork,
  ConstantsHelper,
  IDL as LendingProxyIDL,
  MarketConfigType,
  SOLANA_PROXY_PROGRAM_ID,
  SOLEND_PROGRAM_ID,
  USDS,
  fetchObligationsByAddress,
} from "@renec-foundation/relend-adapter"
import { Connection, Keypair, PublicKey } from "@solana/web3.js"
import BigNumber from "bignumber.js"

const opts: anchor.web3.ConfirmOptions = {
  preflightCommitment: "processed",
  commitment: "processed",
}

const RANDOM_WALLET = [
  190, 75, 84, 190, 125, 219, 236, 57, 16, 61, 92, 84, 166, 249, 35, 240, 50, 231, 236, 146, 117, 24, 228, 166, 224, 56,
  28, 217, 139, 0, 104, 158, 113, 208, 41, 8, 5, 200, 116, 161, 219, 175, 223, 251, 206, 99, 25, 182, 8, 113, 28, 98,
  11, 58, 113, 70, 237, 127, 122, 94, 108, 246, 158, 249,
]
const secretKey = Uint8Array.from(RANDOM_WALLET)
const keypair = Keypair.fromSecretKey(secretKey)
const wallet = new NodeWallet(keypair)

const proxyProgram = new PublicKey(SOLANA_PROXY_PROGRAM_ID)
const solendProgram = new PublicKey(SOLEND_PROGRAM_ID)
const SOLEND_CONFIG = ConstantsHelper.getSolendConfig()[0] as MarketConfigType
const USDS_RESERVE = SOLEND_CONFIG.reserves.find((r) => r.liquidityToken.symbol === USDS)!

export const fetchSolendUSDSObligationStats = async (
  network: BlockchainNetwork,
  connection: Connection,
  selectedSolendPool: SelectedPoolType,
): Promise<SolendUSDSObligationsStatsType> => {
  const provider = new anchor.AnchorProvider(connection, wallet, opts)
  const program = new anchor.Program(LendingProxyIDL, proxyProgram, provider)

  const allUserAuthorities = await program.account.userAuthority.all()
  const obligationAddresses: string[] = []
  for (const ua of allUserAuthorities) {
    const obligation = await PublicKey.createWithSeed(
      new PublicKey(ua.publicKey.toBase58()),
      SOLEND_CONFIG.address.slice(0, 32),
      solendProgram,
    )
    obligationAddresses.push(obligation.toBase58())
  }

  const obligations = await fetchObligationsByAddress(network, obligationAddresses, connection, true)
  const usdsReserveInPool = selectedSolendPool.reserves.find((r) => r.address === USDS_RESERVE.address)
  const formattedObligations = obligations.map((obligation) => {
    // Find the deposit that matches our target reserve
    if (!usdsReserveInPool) return null
    const usdsDeposit = obligation.info.deposits.find(
      (deposit) => deposit.depositReserve.toBase58() === USDS_RESERVE.address,
    )
    const decimals = USDS_RESERVE.liquidityToken.decimals

    return {
      obligationAddress: obligation.pubkey.toBase58(),
      ownerAddress: obligation.info.owner.toBase58(),
      totalSupplyAmount: usdsDeposit
        ? new BigNumber(usdsDeposit.depositedAmount.toString())
            .shiftedBy(-decimals)
            .times(usdsReserveInPool.cTokenExchangeRate)
        : new BigNumber(0),
    }
  })

  const totalSupplyAmount = formattedObligations
    .filter((o): o is SolendUSDSObligationListType => o !== null)
    .reduce((acc, curr) => acc.plus(curr.totalSupplyAmount), new BigNumber(0))

  return {
    totalSupplyAmount,
    obligations: formattedObligations.filter((o): o is SolendUSDSObligationListType => o !== null),
  }
}
